import { useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/primitives";
import { Target } from "lucide-react";
import {
	setReflectionGoal,
	type ReflectionGoal,
} from "@/lib/reflection-progress";

interface ReflectionGoalDialogProps {
	open: boolean;
	onGoalSet: (goal: ReflectionGoal) => void;
	onSkip?: () => void;
	userId: string;
}

export function ReflectionGoalDialog({
	open,
	onGoalSet,
	onSkip,
	userId,
}: ReflectionGoalDialogProps) {
	const [selectedGoal, setSelectedGoal] = useState<ReflectionGoal | null>(null);
	const [saving, setSaving] = useState(false);

	const goals: { value: ReflectionGoal; label: string; description: string }[] =
		[
			{
				value: "1-2",
				label: "1-2 times per week",
				description: "Perfect for getting started with regular reflection",
			},
			{
				value: "3-5",
				label: "3-5 times per week",
				description: "A balanced approach to build a strong habit",
			},
			{
				value: "6+",
				label: "6+ times per week",
				description: "For those committed to daily reflection",
			},
		];

	const handleSave = async () => {
		if (!selectedGoal) return;

		setSaving(true);
		try {
			const { error } = await setReflectionGoal(userId, selectedGoal);
			if (!error) {
				onGoalSet(selectedGoal);
			} else {
				console.error("Error setting goal:", error);
			}
		} catch (error) {
			console.error("Error setting goal:", error);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog
			open={open}
			onOpenChange={(o) => {
				if (!o) {
					onSkip?.();
				}
			}}
			modal={true}
		>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<div className="flex items-center gap-3 mb-2">
						<div className="p-2 rounded-full bg-primary/10">
							<Target className="h-5 w-5 text-primary" />
						</div>
						<DialogTitle className="text-xl">
							Set Your Reflection Goal
						</DialogTitle>
					</div>
					<DialogDescription>
						How many times a week do you aim to write a reflection? This helps
						us track your progress and keep you motivated.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-3 py-4">
					{goals.map((goal) => (
						<button
							key={goal.value}
							onClick={() => setSelectedGoal(goal.value)}
							className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
								selectedGoal === goal.value
									? "border-primary bg-primary/5"
									: "border-gray-200 hover:border-gray-300 dark:border-gray-700 dark:hover:border-gray-600"
							}`}
						>
							<div className="font-medium text-foreground mb-1">
								{goal.label}
							</div>
							<div className="text-sm text-muted-foreground">
								{goal.description}
							</div>
						</button>
					))}
				</div>

				<DialogFooter className="flex-col sm:flex-row gap-2">
					<Button
						onClick={handleSave}
						disabled={!selectedGoal || saving}
						className="w-full sm:flex-1"
					>
						{saving ? "Saving..." : "Set Goal"}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
